/**
 * Design tokens for light and dark themes
 * Generated from Figma design tokens
 */

// Helper function to convert RGB components to rgba string
const rgba = (components: number[], alpha?: number): string => {
  const [r, g, b] = components.map((c) => Math.round(c * 255));
  const a = alpha !== undefined ? alpha : components[3] || 1;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const lightTokens = {
  primary: {
    main: 'rgba(29, 32, 38, 1)', // #1D2026
    light: 'rgba(74, 76, 81, 1)', // #4A4C51
    dark: 'rgba(20, 22, 26, 1)', // #14161A
    contrastText: 'rgba(255, 255, 255, 1)', // #FFFFFF
  },
  secondary: {
    main: '#E6F0FE',
    dark: '#0C2B64',
    contrastText: 'rgba(20, 91, 184, 1)',
  },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
  },
  text: {
    primary: 'rgba(29, 32, 38, 1)', // #1D2026
    secondary: 'rgba(29, 32, 38, 0.6)',
    disabled: 'rgba(29, 32, 38, 0.38)',
  },
  action: {
    active: 'rgba(29, 32, 38, 0.56)',
    hover: 'rgba(29, 32, 38, 0.04)',
    selected: 'rgba(29, 32, 38, 0.08)',
    disabledBackground: 'rgba(29, 32, 38, 0.12)',
    focus: 'rgba(29, 32, 38, 0.12)',
    disabled: 'rgba(29, 32, 38, 0.38)',
  },
  divider: '#1d20261f',
  error: {
    main: 'rgba(184, 20, 62, 1)', // #B8143E - Red60
    light: 'rgba(198, 67, 100, 1)', // #C64364
    dark: 'rgba(128, 14, 43, 1)', // #800E2B
    contrastText: 'rgba(255, 255, 255, 1)',
  },
  warning: {
    main: 'rgba(153, 102, 0, 1)', // #996600 - Yellow70
    light: 'rgba(214, 159, 51, 1)', // #D69F33
    dark: 'rgba(142, 95, 0, 1)', // #8E5F00
    contrastText: '#FFFFFF',
  },
  info: {
    main: 'rgba(20, 91, 184, 1)', // #145BB8 - Blue60
    light: 'rgba(67, 123, 198, 1)', // #437BC6
    dark: 'rgba(14, 63, 128, 1)', // #0E3F80
    contrastText: 'rgba(255, 255, 255, 1)',
  },
  success: {
    main: 'rgba(10, 92, 51, 1)', // #0A5C33 - Green80
    light: 'rgba(59, 124, 91, 1)', // #3B7C5B
    dark: 'rgba(7, 64, 35, 1)', // #074023
    contrastText: 'rgba(255, 255, 255, 1)',
  },
  indigo: {
    main: 'rgba(93, 15, 138, 1)', // #5D0F8A - Indigo70
    light: 'rgba(125, 63, 161, 1)', // #7D3FA1
    dark: 'rgba(65, 10, 96, 1)', // #410A60
    contrastText: '#FFFFFF',
  },
};

export const darkTokens = {
  primary: {
    main: 'rgba(240, 242, 244, 1)', // #F0F2F4 - Gray05
    light: 'rgba(255, 255, 255, 1)', // #FFFFFF
    dark: 'rgba(178, 178, 178, 1)', // #B2B2B2
    contrastText: 'rgba(29, 32, 38, 1)', // #1D2026 - dark text on light primary buttons
  },
  secondary: {
    main: '#1A2332',
    dark: '#0C2B64',
    contrastText: 'rgba(163, 198, 245, 1)', // Lighter blue for dark mode
  },
  background: {
    default: '#1D2026', // Black87 - matches Figma elevation-0
    paper: '#282C34', // Black82 - modals, cards, paper surfaces (elevation-2)
  },
  text: {
    primary: 'rgba(247, 247, 249, 1)', // #F7F7F9 - Gray03
    secondary: 'rgba(247, 247, 249, 0.6)',
    disabled: 'rgba(247, 247, 249, 0.38)',
  },
  action: {
    active: 'rgba(247, 247, 249, 0.56)',
    hover: '#3D4451', // Black70
    selected: '#3D4451', // Black70
    disabledBackground: 'rgba(247, 247, 249, 0.12)',
    focus: 'rgba(247, 247, 249, 0.12)',
    disabled: 'rgba(247, 247, 249, 0.38)',
  },
  divider: 'rgba(247, 247, 249, 0.18)',
  error: {
    main: 'rgba(245, 161, 182, 1)', // #F5A1B6 - Red20
    light: 'rgba(247, 179, 196, 1)', // #F7B3C4
    dark: 'rgba(171, 112, 127, 1)', // #AB707F
    contrastText: 'rgba(29, 32, 38, 1)', // #1D2026 - Black87
  },
  warning: {
    main: 'rgba(255, 220, 153, 1)', // #FFDC99 - Yellow20
    light: 'rgba(255, 227, 173, 1)', // #FFE3AD
    dark: 'rgba(178, 154, 107, 1)', // #B29A6B
    contrastText: 'rgba(29, 32, 38, 1)', // #1D2026 - Black87
  },
  info: {
    main: 'rgba(163, 198, 245, 1)', // #A3C6F5 - Blue20
    light: 'rgba(181, 209, 247, 1)', // #B5D1F7
    dark: 'rgba(114, 138, 171, 1)', // #728AAB
    contrastText: 'rgba(29, 32, 38, 1)', // #1D2026 - Black87
  },
  success: {
    main: 'rgba(163, 245, 204, 1)', // #A3F5CC - Green20
    light: 'rgba(181, 247, 214, 1)', // #B5F7D6
    dark: 'rgba(114, 171, 142, 1)', // #72AB8E
    contrastText: 'rgba(29, 32, 38, 1)', // #1D2026 - Black87
  },
  indigo: {
    main: 'rgba(215, 163, 245, 1)', // #D7A3F5 - Indigo20
    light: 'rgba(223, 181, 247, 1)', // #DFB5F7
    dark: 'rgba(150, 114, 171, 1)', // #9672AB
    contrastText: 'rgba(29, 32, 38, 1)', // #1D2026 - Black87
  },
};

// Common tokens that don't change between themes (use light mode by default)
export const commonTokens = {
  gray: {
    white: '#ffffff',
    gray03: '#F7F7F9',
    gray05: '#F0F2F4',
    gray30: '#a8afbd',
  },
  changeRequested: {
    main: '#0002B0',
  },
  // Dark mode changeRequested (lighter pastel blue for readability/contrast on dark surfaces)
  changeRequestedDark: {
    main: 'rgba(163, 198, 245, 1)', // #A3C6F5 - Blue20, matches darkTokens.info.main
  },
  // Component-specific colors (used in alerts, chips, etc.)
  component: {
    primary: {
      background: '#F0F2F4', // Gray05
      content: '#1D2026', // Black87
    },
    error: {
      background: '#FCE8ED', // Red05
      content: '#8A0F2E', // Red70
    },
    warning: {
      background: '#FFF7E7', // Yellow05
      content: '#664400', // Yellow80
    },
    info: {
      background: '#E8F1FC', // Blue05
      content: '#0F448A', // Blue70
    },
    success: {
      background: '#E6FCF1', // Green05
      content: '#0A5C33', // Green80
    },
    indigo: {
      background: '#F5E8FC', // Indigo05
      content: '#3E0A5C', // Indigo80
    },
  },
  // Dark mode component colors
  componentDark: {
    primary: {
      background: '#2C313A', // Black80
      content: '#F0F2F4', // Gray05
    },
    error: {
      background: '#8A0F2E', // Red70
      content: '#FCE8ED', // Red05
    },
    warning: {
      background: '#664400', // Yellow80
      content: '#FFF7E7', // Yellow05
    },
    info: {
      background: '#0A2E5C', // Blue80
      content: '#E8F1FC', // Blue05
    },
    success: {
      background: '#0A5C33', // Green80
      content: '#E6FCF1', // Green05
    },
    indigo: {
      background: '#3E0A5C', // Indigo80
      content: '#F5E8FC', // Indigo05
    },
  },
};

// Extended palette colors for specific use cases
export const paletteExtensions = {
  light: {
    error: {
      200: '#FDB1CA',
      400: '#FB6395',
      600: '#A60E40',
      800: '#530720',
    },
    success: {
      200: '#B3F7D5',
      400: '#67EFAC',
      600: '#129A57',
      800: '#094D2B',
    },
    info: {
      200: '#AACEFD',
      400: '#559DFB',
      600: '#0048A6',
      800: '#002453',
    },
    warning: {
      200: '#FDE5AA',
      400: '#FBCB55',
      600: '#A67600',
      800: '#533B00',
    },
  },
  dark: {
    // Dark mode uses the same palette extensions
    error: {
      200: '#FDB1CA',
      400: '#FB6395',
      600: '#A60E40',
      800: '#530720',
    },
    success: {
      200: '#B3F7D5',
      400: '#67EFAC',
      600: '#129A57',
      800: '#094D2B',
    },
    info: {
      200: '#AACEFD',
      400: '#559DFB',
      600: '#0048A6',
      800: '#002453',
    },
    warning: {
      200: '#FDE5AA',
      400: '#FBCB55',
      600: '#A67600',
      800: '#533B00',
    },
  },
};
